import { useState } from "react";
import { InputOwnProps } from ".";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  Box,
  IconButton,
  InputAdornment,
  styled,
  TextField,
  Typography,
} from "@mui/material";

export type InputProps = InputOwnProps &
  React.ComponentProps<typeof TextField> & {
    customLabelStyles?: boolean; // Add this prop to conditionally apply label styles
    isValid?: boolean; // Add prop to indicate valid state
  };

const CssTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "icon" && prop !== "customize" && prop !== "customLabelStyles",
})<{
  icon?: boolean;
  customize?: boolean;
  customLabelStyles?: boolean; // Add this to styled component
  isValid?: boolean; // Add prop to indicate valid state
}>(({ customize, icon, customLabelStyles, isValid }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: "16px",
    border: " 1px solid #ECEBF2",
  },

  "& .MuiFilledInput-root:after, .MuiFilledInput-root:before": {
    display: "none",
  },
  "& .MuiFilledInput-root .Mui-disabled": {
    backgroundColor: "#ececec !important",
    opacity: "0.5",
    borderRadius: "16px",
  },
  "& .Mui-focused": {
    backgroundColor: "#ffffff !important",
  },

  "& .MuiFilledInput-input": {
    direction: "inherit",
    padding: customize ? "16px" : "25px 12px 8px 12px",
    color: isValid ? "#0A0A0E !important" : "#00000099 !important",
    fontWeight: isValid ? "500 !important" : "400 !important",
    WebkitTextFillColor: isValid ? "#0A0A0E !important" : "#00000099 !important",
  },
  // Conditionally apply label styles only when customLabelStyles is true
  ...(customLabelStyles && {
    "& .MuiFormLabel-root": {
      left: icon && "0px",
      color: "#0A0A0E !important",
      fontWeight: "700",
      fontSize: "15px",
      lineHeight: "18px",
      paddingLeft: "7px",
      marginTop: "5px",
    },
    "& .MuiFilledInput-root": {
      height: "65px",
      backgroundColor: "#ffff",
      borderRadius: "16px",
      paddingLeft: "6px",
    },
  }),
  "& .MuiFormHelperText-root": {
    color: "rgb(183, 33, 54)",
  },
  "@media (max-width: 899.95px)": {
    "& .MuiFilledInput-input": {
      fontSize: "16px",
    },
  },
}));

export default function Input({
  type,
  showIcon,
  StartIcon,
  StartIconProps,
  error,
  helperText,
  customize,
  customLabelStyles, // Add this prop
  isValid, // Add this prop
  placeholder,
  onFocus,
  ...rest
}: InputProps) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false); // Track if the field is focused

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  return (
    <>
      {StartIcon ? (
        <>
          <Box
            sx={{
              display: "flex",
              position: "relative",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                left: "13px",
                top: "16px",
                position: "absolute",
                zIndex: 1,
              }}
            >
              {/* Optional Start Icon */}
            </Box>
            <CssTextField
              fullWidth
              variant="filled"
              icon={true}
              customLabelStyles={customLabelStyles} // Pass the prop
              isValid={isValid} // Pass the prop
              onFocus={handleFocus}
              onBlur={handleBlur}
              {...rest}
            />
          </Box>
          {!focused && error && helperText && (
            <div
              style={{
                position: "absolute",
                marginLeft: "14px",
                color: "rgb(183, 33, 54)",
                fontSize: "0.75rem",
                lineHeight: "1.66",
                fontWeight: "400",
                margin:"2px"
              }}
            >
              {helperText}
            </div>
          )}
        </>
      ) : (
        <>
          {customize && (
            <Typography
              mb={1}
              color={"#00000099"}
              fontSize={"12px"}
              fontWeight={400}
              textTransform={"uppercase"}
            >
              {rest?.label}
            </Typography>
          )}
          <CssTextField
            customize={customize}
            customLabelStyles={customLabelStyles} // Pass the prop here too
            isValid={isValid} // Pass the prop
            fullWidth
            color="info"
            variant="filled"
            className="customize"
            placeholder={placeholder}
            type={show && showIcon && type === "password" ? "text" : type}
            {...rest}
            label={!customize ? rest.label : ""}
            InputProps={{
              endAdornment: showIcon && type === "password" && (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setShow(!show)}
                    onMouseDown={(e) => e.preventDefault()}
                    aria-label="toggle password visibility"
                  >
                    {show ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              ...rest.InputProps,
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {!focused && error && helperText && (
            <div
              style={{
                position: "absolute",
                marginLeft: "14px",
                color: "rgb(183, 33, 54)",
                fontSize: "0.75rem",
                lineHeight: "1.66",
                fontWeight: "400",
                margin:"2px"
              }}
            >
              {helperText}
            </div>
          )}
        </>
      )}
    </>
  );
}
